import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Clock, Gauge, Info, Loader2, Split, Users, UsersRound } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DataTable, type DataTableState } from '@/components/DataTable';
import { TransferPicker, toggleIn } from '@/components/TransferPicker';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, ModalError } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toast';
import { ApiError } from '@/lib/api';
import {
  fetchEmployeeLinks,
  fetchGroupLinks,
  fetchMachineReach,
  updateEmployeeLinks,
  updateGroupLinks,
  type MachineReach,
} from '@/lib/assignments-api';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';
import { useIslands } from '@/lib/islands';
import { cn } from '@/lib/utils';

type Tab = 'employees' | 'groups';

// ─────────────────────────── modal przypisań ─────────────────────────────────

function LinksModal({
  machine,
  islandId,
  initialTab,
  onClose,
  onSaved,
}: {
  machine: MachineReach;
  islandId: string;
  initialTab: Tab;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const [tab, setTab] = useState<Tab>(initialTab);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [toAdd, setToAdd] = useState<Set<string>>(new Set());
  const [toRemove, setToRemove] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(id);
  }, [search]);

  // przełączenie zakładki to inna relacja — zaznaczenia z poprzedniej nie mogą przeciekać
  const switchTab = (next: Tab) => {
    setTab(next);
    setToAdd(new Set());
    setToRemove(new Set());
    setSearch('');
    setError(null);
  };

  const employees = useQuery({
    queryKey: ['assignments', machine.id, 'employees', debounced],
    queryFn: () => fetchEmployeeLinks(machine.id, islandId, debounced),
    enabled: tab === 'employees',
  });

  const groups = useQuery({
    queryKey: ['assignments', machine.id, 'groups'],
    queryFn: () => fetchGroupLinks(machine.id, islandId),
    enabled: tab === 'groups',
  });

  const dirty = toAdd.size + toRemove.size > 0;

  /**
   * Ilu osób DOTKNIE zmiana w zakładce grup — grupa wpuszcza cały skład naraz.
   *
   * SUMA, nie różnica. W 4.6 poprawiłem samą etykietę („Netto zmiana osób" → „Osób w zmienianych
   * grupach"), zostawiając obliczenie netto — czyli etykieta nadal opisywała co innego, niż liczyła.
   * Dodanie grupy 50-osobowej i odebranie 30-osobowej dotyka 80 osób, a nie 20; przy dwóch grupach
   * po 40 osób różnica wychodziła 0 i licznik znikał, mimo że zmiana dotykała 80 osób.
   */
  const headsTouched = useMemo(() => {
    if (tab !== 'groups' || !groups.data) return null;
    const all = [...groups.data.assigned, ...groups.data.candidates];
    const sum = (ids: Set<string>) =>
      [...ids].reduce((n, id) => n + (all.find((g) => g.id === id)?.members ?? 0), 0);
    return sum(toAdd) + sum(toRemove);
  }, [tab, groups.data, toAdd, toRemove]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = { groupId: islandId, add: [...toAdd], remove: [...toRemove] };
      if (tab === 'employees') await updateEmployeeLinks(machine.id, payload);
      else await updateGroupLinks(machine.id, payload);
      toast.success(t('asg.saved'));
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        title={`${t('asg.title')} — ${machine.name}`}
        description={`VMID=${machine.vmid} · ${t('asg.reach')}: ${machine.effectiveReach}`}
        className="w-[min(58rem,calc(100vw-2rem))]"
      >
        <div className="mb-3 flex overflow-hidden rounded-md border border-border">
          {(['employees', 'groups'] as Tab[]).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => switchTab(k)}
              aria-pressed={tab === k}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 text-xs transition-colors',
                tab === k ? 'bg-primary text-primary-foreground' : 'hover:bg-accent',
              )}
            >
              {k === 'employees' ? <Users className="size-3.5" /> : <UsersRound className="size-3.5" />}
              {t(`asg.tab.${k}`)}
            </button>
          ))}
        </div>

        {tab === 'groups' && (
          <div className="mb-3 flex items-start gap-2 rounded-md border border-[var(--warning)]/40 bg-[var(--warning)]/10 p-3 text-xs">
            <Info className="mt-0.5 size-3.5 shrink-0" />
            <span>
              {t('asg.groupWarn')}
              {headsTouched !== null && headsTouched > 0 && (
                <>
                  {' '}
                  <span className="font-medium" title={t('asg.netHeadsHint')}>
                    {t('asg.netHeads')}: {headsTouched}
                  </span>{' '}
                  <span className="text-muted-foreground">{t('asg.netHeadsHint')}</span>
                </>
              )}
            </span>
          </div>
        )}

        {tab === 'employees' ? (
          <TransferPicker
            assignedTitle={t('asg.assignedEmployees')}
            availableTitle={t('asg.availableEmployees')}
            assigned={(employees.data?.assigned ?? []).map((e) => ({
              id: e.id,
              key: e.spsUserNumber,
              primary: e.name,
              badge:
                e.machineCount > 1 ? (
                  <Badge variant="warning" title={t('emp.multiHint')}>
                    <Split className="size-2.5" />
                    {e.machineCount}
                  </Badge>
                ) : undefined,
            }))}
            candidates={(employees.data?.candidates ?? []).map((e) => ({
              id: e.id,
              key: e.spsUserNumber,
              primary: e.name,
            }))}
            toAdd={toAdd}
            toRemove={toRemove}
            onToggleAdd={(id) => toggleIn(setToAdd, id)}
            onToggleRemove={(id) => toggleIn(setToRemove, id)}
            search={search}
            onSearchChange={setSearch}
            capNote={t('grp.candidatesCapped')}
            loadingAssigned={employees.isPending}
            loadingCandidates={employees.isPending}
            emptyAssigned={t('asg.noEmployees')}
            emptyCandidates={t('grp.noCandidates')}
          />
        ) : (
          <TransferPicker
            assignedTitle={t('asg.assignedGroups')}
            availableTitle={t('asg.availableGroups')}
            assigned={(groups.data?.assigned ?? []).map((g) => ({
              id: g.id,
              key: g.spsGroupNumber,
              primary: `${g.name} (${g.members})`,
              badge:
                g.rationing > 0 ? (
                  <Badge variant="neutral" title={t('grp.limitsHint')}>
                    <Gauge className="size-2.5" />
                    {g.rationing}
                  </Badge>
                ) : undefined,
            }))}
            candidates={(groups.data?.candidates ?? []).map((g) => ({
              id: g.id,
              key: g.spsGroupNumber,
              primary: `${g.name} (${g.members})`,
              badge:
                g.rationing > 0 ? (
                  <Badge variant="neutral" title={t('grp.limitsHint')}>
                    <Gauge className="size-2.5" />
                    {g.rationing}
                  </Badge>
                ) : undefined,
            }))}
            toAdd={toAdd}
            toRemove={toRemove}
            onToggleAdd={(id) => toggleIn(setToAdd, id)}
            onToggleRemove={(id) => toggleIn(setToRemove, id)}
            search={search}
            onSearchChange={setSearch}
            loadingAssigned={groups.isPending}
            loadingCandidates={groups.isPending}
            emptyAssigned={t('asg.noGroups')}
            emptyCandidates={t('asg.allGroupsAssigned')}
          />
        )}

        <div className="mt-3">
          <ModalError>{error}</ModalError>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="button" disabled={!dirty || saving} onClick={() => void save()}>
            {saving && <Loader2 className="animate-spin" />}
            {dirty ? `${t('common.save')} (+${toAdd.size} −${toRemove.size})` : t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────── strona ──────────────────────────────────────────

export function Assignments() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const { isAdmin } = useAuth();
  const { groupId, island } = useIslands();

  const [state, setState] = useState<DataTableState>({
    search: '',
    page: 1,
    pageSize: 25,
    sort: 'vmid',
    dir: 'asc',
  });
  const [openFor, setOpenFor] = useState<{ machine: MachineReach; tab: Tab } | null>(null);

  const { data, isPending, isFetching, refetch } = useQuery({
    queryKey: ['assignments', 'machines', groupId],
    queryFn: () => fetchMachineReach(groupId!),
    enabled: Boolean(groupId),
  });

  const onStateChange = useCallback((patch: Partial<DataTableState>) => setState((s) => ({ ...s, ...patch })), []);

  // zasięg liczy backend jednym zapytaniem na wyspę — filtrowanie/sort robimy lokalnie
  const rows = useMemo(() => {
    const q = state.search.trim().toLowerCase();
    const filtered = (data ?? []).filter(
      (r) => !q || r.name.toLowerCase().includes(q) || String(r.vmid).includes(q),
    );
    const dir = state.dir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const key = state.sort as keyof MachineReach;
      const av = a[key];
      const bv = b[key];
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv), 'pl') * dir;
    });
  }, [data, state.search, state.sort, state.dir]);

  const pageRows = rows.slice((state.page - 1) * state.pageSize, state.page * state.pageSize);

  const columns = useMemo<ColumnDef<MachineReach>[]>(
    () => [
      {
        id: 'vmid',
        header: 'VMID',
        meta: { exportValue: (r) => r.vmid, align: 'right' },
        cell: ({ row }) => <span className="font-mono text-xs">{row.original.vmid}</span>,
      },
      {
        id: 'name',
        header: t('asg.machine'),
        meta: { exportValue: (r) => r.name },
        cell: ({ row }) => (
          <span className="flex items-center gap-2">
            <span className="font-medium">{row.original.name}</span>
            {row.original.pending && (
              <Badge variant="warning" title={t('asg.pendingHint')}>
                <Clock className="size-3" />
                {t('machines.pending')}
              </Badge>
            )}
          </span>
        ),
      },
      {
        id: 'directEmployees',
        header: t('asg.direct'),
        meta: { exportValue: (r) => r.directEmployees, align: 'right' },
        cell: ({ row }) => <span className="tabular-nums">{row.original.directEmployees}</span>,
      },
      {
        id: 'groups',
        header: t('asg.groups'),
        meta: { exportValue: (r) => r.groups, align: 'right' },
        cell: ({ row }) => <span className="tabular-nums">{row.original.groups}</span>,
      },
      {
        id: 'effectiveReach',
        header: t('asg.reach'),
        meta: { exportValue: (r) => r.effectiveReach, align: 'right' },
        cell: ({ row }) => (
          <span className="font-medium tabular-nums" title={t('asg.reachHint')}>
            {row.original.effectiveReach}
          </span>
        ),
      },
      {
        id: 'alsoOnOtherMachines',
        header: t('asg.alsoOther'),
        meta: { exportValue: (r) => r.alsoOnOtherMachines, align: 'right' },
        cell: ({ row }) =>
          row.original.alsoOnOtherMachines > 0 ? (
            <Badge variant="warning" title={t('asg.alsoOtherHint')}>
              <Split className="size-3" />
              {row.original.alsoOnOtherMachines}
            </Badge>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: 'actions',
        header: t('table.actions'),
        enableSorting: false,
        meta: { align: 'right' },
        cell: ({ row }) => (
          <div className="flex justify-end gap-1">
            <Button
              variant="ghost"
              size="icon"
              title={t('asg.tab.employees')}
              aria-label={`${t('asg.tab.employees')} ${row.original.name}`}
              onClick={() => setOpenFor({ machine: row.original, tab: 'employees' })}
            >
              <Users />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              title={t('asg.tab.groups')}
              aria-label={`${t('asg.tab.groups')} ${row.original.name}`}
              onClick={() => setOpenFor({ machine: row.original, tab: 'groups' })}
            >
              <UsersRound />
            </Button>
          </div>
        ),
      },
    ],
    [t],
  );

  if (!groupId) return <p className="text-sm text-muted-foreground">{t('islands.none')}</p>;

  return (
    <div className="space-y-4">
      {/*
        ⚠️ BEZ AKAPITU O ZASIĘGU (zgłoszone 01.09.2026). Nazwa kolumny plus liczba mówią tyle samo;
        definicja („różni pracownicy, nie suma kolumn") stoi w komentarzu przy jej wyliczeniu.
      */}
      <DataTable<MachineReach>
        title={t('nav.assignments')}
        columns={columns}
        data={pageRows}
        total={rows.length}
        state={state}
        onStateChange={onStateChange}
        isLoading={isPending}
        isFetching={isFetching}
        onRefresh={() => void refetch()}
        rowKey={(r) => r.id}
        exportFileName={`przypisania-${island?.code ?? 'wyspa'}`}
        fetchAllRows={async () => rows}
        emptyText={t('table.empty')}
      />

      {openFor && (
        <LinksModal
          key={`${openFor.machine.id}-${openFor.tab}`}
          machine={openFor.machine}
          islandId={groupId}
          initialTab={openFor.tab}
          onClose={() => setOpenFor(null)}
          onSaved={() => {
            void qc.invalidateQueries({ queryKey: ['assignments'] });
            void qc.invalidateQueries({ queryKey: ['employees'] });
            void qc.invalidateQueries({ queryKey: ['employee-groups'] });
          }}
        />
      )}

      {!isAdmin && <p className="text-xs text-muted-foreground">{t('asg.readOnlyForYou')}</p>}
    </div>
  );
}
